import type { HermesGuardFn } from '@/router/guards/types'

const LEGACY_GBP_FLOW = 'gbp'
const SETUP_FLOW_ROUTE_NAMES = new Set([
  'brand.setup.flow',
  'brand.setup.flow.step',
])

// Repeatable route params and query keys both arrive as arrays, and a bare
// `?locationId` contributes a null entry — so take the first usable value
// rather than index 0.
const getFirstString = (value: unknown): string | undefined => {
  if (Array.isArray(value)) {
    return value.find(
      (entry): entry is string => typeof entry === 'string' && entry.length > 0
    )
  }

  return typeof value === 'string' && value.length > 0 ? value : undefined
}

/**
 * Redirects the deleted brand-level `/setup/gbp` wizard URL into the
 * per-location GBP flow. Stale entry points still produce the old URL —
 * bookmarks, already-sent nudge emails/SMS, and in-flight sessions — and
 * without a redirect they would land on an unrenderable flow
 * (`flowSteps.gbp` no longer exists).
 *
 * A link that carries a locationId (Command Center and nudges always did)
 * lands on the same step of the per-location flow; without one the merchant
 * is sent to setup home to enter through a card.
 * @param to
 */
export const checkLegacyGbpFlowRedirect: HermesGuardFn = async (to) => {
  if (typeof to.name !== 'string' || !SETUP_FLOW_ROUTE_NAMES.has(to.name)) {
    return undefined
  }

  if (getFirstString(to.params.flow) !== LEGACY_GBP_FLOW) {
    return undefined
  }

  const brandId = getFirstString(to.params.brandId)
  const locationId = getFirstString(to.query.locationId)

  if (!locationId) {
    return { name: 'brand.setup', params: { brandId } }
  }

  return {
    name: 'location.setup.flow.step',
    params: {
      brandId,
      flow: 'gbp-per-location',
      locationId,
      step: getFirstString(to.params.step) ?? 'landing',
    },
    query: { ...to.query, locationId: undefined },
  }
}
